import { ChangeEvent } from 'react';
import {
  Box,
  HStack,
  VStack,
  Text,
  Icon,
  Switch,
  Badge,
  IconButton,
  Button,
  Divider,
  Select as ChakraSelect,
  Input as ChakraInput,
} from '@chakra-ui/react';
import {
  RiUserLine,
  RiUserAddLine,
  RiDeleteBinLine,
  RiBuildingLine,
  RiBriefcaseLine,
  RiMapPinLine,
  RiBankCardLine,
} from 'react-icons/ri';
import { v4 as uuid } from 'uuid';

import { UserSelect } from '../../../Global/UserSelect';
import { SearchableSelect } from '../SearchableSelect';
import { IUserOption, IBranchOption } from '../../../../services/usersService';
import { IExpenseParticipant } from '../../../../pages/Dashboard/vehicles_management/Expenses';
import {
  ORG_SECTORS,
  COST_CENTERS,
  getSectionsBySector,
} from '../../../../constants/orgStructure';

// Opções de Setor e Centro de Custo (fixas). Seção é derivada do setor.
const SECTOR_OPTIONS = ORG_SECTORS.map(s => ({
  value: s.code,
  label: s.name ? `${s.code} – ${s.name}` : s.code,
}));

const COST_CENTER_OPTIONS = COST_CENTERS.map(c => ({
  value: c.code,
  label: `${c.code} – ${c.name}`,
}));

interface IExpenseParticipantsProps {
  participants: IExpenseParticipant[];
  onChange: (participants: IExpenseParticipant[]) => void;
  users: IUserOption[];
  branches: IBranchOption[];
}

export function createEmptyParticipant(
  role: IExpenseParticipant['role'] = 'related',
): IExpenseParticipant {
  return {
    id: uuid(),
    user_id: '',
    role,
    sector: '',
    section: '',
    cost_center: '',
    branch_id: '',
    payment_required: true,
    pix_key: '',
    bank_name: '',
    bank_agency: '',
    bank_account: '',
  };
}

const labelColor = '#475569';
const fieldBorder = '#cbd5e1';

export function ExpenseParticipants({
  participants,
  onChange,
  users,
  branches,
}: IExpenseParticipantsProps) {
  const selectedUserIds = participants.map(p => p.user_id).filter(Boolean);

  const updateParticipant = (
    id: string,
    patch: Partial<IExpenseParticipant>,
  ) => {
    onChange(participants.map(p => (p.id === id ? { ...p, ...patch } : p)));
  };

  // Handlers de campo compactos (mantêm o onChange em uma única linha)
  const onFieldChange =
    (id: string, field: keyof IExpenseParticipant) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      updateParticipant(id, { [field]: e.target.value });

  const onPaymentToggle = (id: string) => (e: ChangeEvent<HTMLInputElement>) =>
    updateParticipant(id, { payment_required: e.target.checked });

  // Handler para os SearchableSelect (recebem o valor direto, não um evento).
  const onSearchableChange =
    (id: string, field: keyof IExpenseParticipant) => (value: string) =>
      updateParticipant(id, { [field]: value });

  // Ao selecionar um usuário, substitui lotação e dados bancários pelos
  // do usuário recém-selecionado (em branco se ele não tiver), para não
  // vazar dados de uma pessoa pra outra ao trocar o usuário selecionado.
  const handleUserChange = (id: string, userId: string) => {
    const user = users.find(u => u.id === userId);
    const current = participants.find(p => p.id === id);
    if (!current) return;

    const patch: Partial<IExpenseParticipant> = {
      user_id: userId,
      name: user?.name || '',
      sector: user?.sector || '',
      section: user?.section || '',
      cost_center: user?.cost_center || '',
      branch_id: user?.branch_id || '',
      pix_key: user?.pix_key || '',
      bank_name: user?.bank_name || '',
      bank_agency: user?.bank_agency || '',
      bank_account: user?.bank_account || '',
    };
    updateParticipant(id, patch);
  };

  const handleAddRelated = () => {
    onChange([...participants, createEmptyParticipant('related')]);
  };

  const handleRemove = (id: string) => {
    onChange(participants.filter(p => p.id !== id));
  };

  const renderCard = (participant: IExpenseParticipant, index: number) => {
    const isResponsible = participant.role === 'responsible';
    // Usuários já escolhidos em outros participantes ficam indisponíveis
    const availableUsers = users.filter(
      u => u.id === participant.user_id || !selectedUserIds.includes(u.id),
    );

    return (
      <Box
        key={participant.id}
        p={4}
        bg="white"
        borderRadius="md"
        borderWidth={1}
        borderColor={isResponsible ? '#1a5490' : '#e2e8f0'}
        borderLeftWidth="4px"
        borderLeftColor={isResponsible ? '#1a5490' : '#94a3b8'}
        boxShadow="sm"
      >
        <HStack justify="space-between" mb={3}>
          <HStack spacing={2}>
            <Icon
              as={RiUserLine}
              color={isResponsible ? '#1a5490' : '#64748b'}
            />
            <Badge
              colorScheme={isResponsible ? 'blue' : 'gray'}
              borderRadius="md"
              px={2}
            >
              {isResponsible ? 'Responsável' : `Vinculado ${index}`}
            </Badge>
          </HStack>
          {!isResponsible && (
            <IconButton
              aria-label="Remover vinculado"
              icon={<RiDeleteBinLine />}
              size="sm"
              colorScheme="red"
              variant="ghost"
              onClick={() => handleRemove(participant.id)}
            />
          )}
        </HStack>

        <VStack spacing={3} align="stretch">
          <Box>
            <Text fontSize="xs" fontWeight="600" color={labelColor} mb={1}>
              Usuário
            </Text>
            <UserSelect
              users={availableUsers}
              value={participant.user_id}
              onChange={userId => handleUserChange(participant.id, userId)}
              placeholder="Pesquisar e selecionar usuário"
            />
          </Box>

          <HStack spacing={3} align="flex-start">
            <Box flex={1}>
              <HStack spacing={1} mb={1}>
                <Icon as={RiBuildingLine} fontSize="13" color={labelColor} />
                <Text fontSize="xs" fontWeight="600" color={labelColor}>
                  Filial
                </Text>
              </HStack>
              <ChakraSelect
                size="sm"
                placeholder="Selecione"
                value={participant.branch_id || ''}
                onChange={onFieldChange(participant.id, 'branch_id')}
                color="#1a202c"
                borderColor={fieldBorder}
              >
                {branches.map(b => (
                  <option key={b.id} value={b.id}>
                    {b.short_name}
                  </option>
                ))}
              </ChakraSelect>
            </Box>
            <Box flex={1}>
              <HStack spacing={1} mb={1}>
                <Icon as={RiBuildingLine} fontSize="13" color={labelColor} />
                <Text fontSize="xs" fontWeight="600" color={labelColor}>
                  Setor
                </Text>
              </HStack>
              <SearchableSelect
                options={SECTOR_OPTIONS}
                value={participant.sector}
                placeholder="Pesquisar setor"
                borderColor={fieldBorder}
                onChange={sector => {
                  // Ao trocar o setor, limpa a seção se ela não pertencer mais a ele.
                  const stillValid = getSectionsBySector(sector).some(
                    s => s.code === participant.section,
                  );
                  updateParticipant(participant.id, {
                    sector,
                    section: stillValid ? participant.section : '',
                  });
                }}
              />
            </Box>
          </HStack>

          <HStack spacing={3} align="flex-start">
            <Box flex={1}>
              <HStack spacing={1} mb={1}>
                <Icon as={RiBriefcaseLine} fontSize="13" color={labelColor} />
                <Text fontSize="xs" fontWeight="600" color={labelColor}>
                  Seção
                </Text>
              </HStack>
              <SearchableSelect
                options={getSectionsBySector(participant.sector).map(s => ({
                  value: s.code,
                  label: `${s.code} – ${s.name}`,
                }))}
                value={participant.section}
                placeholder={
                  participant.sector
                    ? 'Pesquisar seção'
                    : 'Selecione o setor primeiro'
                }
                isDisabled={!participant.sector}
                borderColor={fieldBorder}
                emptyMessage="Nenhuma seção para este setor"
                onChange={onSearchableChange(participant.id, 'section')}
              />
            </Box>
            <Box flex={1}>
              <HStack spacing={1} mb={1}>
                <Icon as={RiMapPinLine} fontSize="13" color={labelColor} />
                <Text fontSize="xs" fontWeight="600" color={labelColor}>
                  Centro de Custo
                </Text>
              </HStack>
              <SearchableSelect
                options={COST_CENTER_OPTIONS}
                value={participant.cost_center}
                placeholder="Pesquisar centro de custo"
                borderColor={fieldBorder}
                onChange={onSearchableChange(participant.id, 'cost_center')}
              />
            </Box>
          </HStack>

          <Divider borderColor="gray.200" />

          <HStack justify="space-between" p={2} bg="#f8fafc" borderRadius="md">
            <HStack spacing={2}>
              <Icon as={RiBankCardLine} fontSize="14" color={labelColor} />
              <Text fontSize="xs" fontWeight="600" color={labelColor}>
                Pagamento necessário para este usuário?
              </Text>
            </HStack>
            <HStack spacing={2}>
              <Switch
                size="sm"
                colorScheme="green"
                isChecked={participant.payment_required}
                onChange={onPaymentToggle(participant.id)}
              />
              <Text
                fontSize="xs"
                color={participant.payment_required ? '#16a34a' : '#94a3b8'}
              >
                {participant.payment_required
                  ? 'Sim'
                  : 'Não (responsável arcou)'}
              </Text>
            </HStack>
          </HStack>

          {participant.payment_required && (
            <VStack spacing={2} align="stretch">
              <Box>
                <Text fontSize="xs" color={labelColor} mb={1}>
                  Chave PIX
                </Text>
                <ChakraInput
                  size="sm"
                  placeholder="CPF, Email, Telefone ou Chave"
                  value={participant.pix_key || ''}
                  onChange={onFieldChange(participant.id, 'pix_key')}
                  color="#1a202c"
                  borderColor={fieldBorder}
                />
              </Box>
              <HStack spacing={3}>
                <Box flex={1}>
                  <Text fontSize="xs" color={labelColor} mb={1}>
                    Banco
                  </Text>
                  <ChakraInput
                    size="sm"
                    placeholder="Nome do banco"
                    value={participant.bank_name || ''}
                    onChange={onFieldChange(participant.id, 'bank_name')}
                    color="#1a202c"
                    borderColor={fieldBorder}
                  />
                </Box>
                <Box flex={1}>
                  <Text fontSize="xs" color={labelColor} mb={1}>
                    Agência
                  </Text>
                  <ChakraInput
                    size="sm"
                    placeholder="0000"
                    value={participant.bank_agency || ''}
                    onChange={onFieldChange(participant.id, 'bank_agency')}
                    color="#1a202c"
                    borderColor={fieldBorder}
                  />
                </Box>
                <Box flex={1}>
                  <Text fontSize="xs" color={labelColor} mb={1}>
                    Conta
                  </Text>
                  <ChakraInput
                    size="sm"
                    placeholder="00000-0"
                    value={participant.bank_account || ''}
                    onChange={onFieldChange(participant.id, 'bank_account')}
                    color="#1a202c"
                    borderColor={fieldBorder}
                  />
                </Box>
              </HStack>
            </VStack>
          )}
        </VStack>
      </Box>
    );
  };

  // Índice apenas entre os vinculados (para rótulo "Vinculado N")
  let relatedCounter = 0;

  return (
    <VStack spacing={3} align="stretch">
      {participants.map(participant => {
        if (participant.role === 'related') relatedCounter += 1;
        return renderCard(
          participant,
          participant.role === 'related' ? relatedCounter : 0,
        );
      })}

      <Button
        size="sm"
        variant="outline"
        colorScheme="blue"
        alignSelf="flex-start"
        leftIcon={<Icon as={RiUserAddLine} />}
        onClick={handleAddRelated}
      >
        Adicionar Usuário Vinculado
      </Button>
    </VStack>
  );
}
